import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { api, apiPdfUrl } from '@/lib/api';
import { useI18n, formatCurrency } from '@/lib/i18n';
import { useBranding } from '@/lib/branding';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft, Plus, Send, Printer, X, CreditCard, Receipt as ReceiptIcon, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

/**
 * Trigger silent print of a PDF using a hidden iframe.
 * Works with any OS-installed printer (thermal POS or A4) and lets the
 * browser print dialog auto-select the default printer if the OS allows.
 */
function printPdfInIframe(url) {
  const existing = document.getElementById('__pos_print_iframe');
  if (existing) existing.remove();
  const iframe = document.createElement('iframe');
  iframe.id = '__pos_print_iframe';
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.src = url;
  iframe.onload = () => {
    setTimeout(() => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch (_) {
        // fallback: open in new tab
        window.open(url, '_blank');
      }
    }, 300);
    setTimeout(() => iframe.remove(), 30_000);
  };
  document.body.appendChild(iframe);
}

const STATUS_RING = {
  libre: 'ring-transparent',
  ocupada: 'ring-[hsl(var(--secondary))]',
  cuenta_solicitada: 'ring-[hsl(43_92%_55%)]',
  reservada: 'ring-[hsl(199_70%_55%)]',
};

function TableTile({ table, onClick, t }) {
  return (
    <button
      onClick={onClick}
      data-testid={`pos-table-tile-${table.numero}`}
      className={cn(
        'rounded-2xl border-2 bg-[hsl(var(--card))] p-4 text-left transition-all',
        'hover:shadow-md active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[hsl(var(--ring))]',
        'ring-2 ring-offset-2 ring-offset-[hsl(var(--background))]',
        STATUS_RING[table.estado] || 'ring-transparent'
      )}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="font-heading font-bold text-2xl">{table.numero}</div>
          {table.nombre && <div className="text-xs text-[hsl(var(--muted-foreground))]">{table.nombre}</div>}
        </div>
        <Badge variant="outline" className="text-xs">
          {table.capacidad}p
        </Badge>
      </div>
      <div className="text-xs text-[hsl(var(--muted-foreground))]">
        {t(`table_status_${table.estado}`)}
      </div>
      <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">{table.zona}</div>
    </button>
  );
}

export default function Pos() {
  const { t, lang } = useI18n();
  const { branding } = useBranding();
  const autoPrint = !!branding?.auto_print_kitchen;
  const printerSize = branding?.kitchen_printer_size || '80mm';
  const [tables, setTables] = useState([]);
  const [activeOrder, setActiveOrder] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [createTableOpen, setCreateTableOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closePayload, setClosePayload] = useState({
    tipo: 'ticket',
    pago_metodo: 'tarjeta',
    cliente_nombre: '',
    cliente_nif: '',
    cliente_direccion: '',
  });

  const loadTables = useCallback(async () => {
    const r = await api.get('/tables');
    setTables(r.data);
  }, []);

  const loadMenu = useCallback(async () => {
    const [c, m] = await Promise.all([api.get('/menu-categories'), api.get('/menu-items', { params: { activo: true } })]);
    setCategories(c.data);
    setMenuItems(m.data);
    setActiveCategory((prev) => prev || (c.data[0]?.id ?? null));
  }, []);

  useEffect(() => {
    loadTables();
    loadMenu();
  }, [loadTables, loadMenu]);

  const openTable = async (table) => {
    if (table.orden_actual_id) {
      const r = await api.get(`/orders/${table.orden_actual_id}`);
      setActiveOrder(r.data);
      return;
    }
    const r = await api.post('/orders', {
      table_id: table.id,
      tipo: 'mesa',
      comensales: 1,
    });
    setActiveOrder(r.data);
    loadTables();
  };

  const addItem = async (mi) => {
    if (!activeOrder) return;
    const r = await api.post(`/orders/${activeOrder.id}/items`, {
      menu_item_id: mi.id,
      cantidad: 1,
    });
    const updated = await api.get(`/orders/${activeOrder.id}`);
    setActiveOrder(updated.data);
  };

  const removeItem = async (iid) => {
    if (!activeOrder) return;
    await api.delete(`/orders/${activeOrder.id}/items/${iid}`);
    const updated = await api.get(`/orders/${activeOrder.id}`);
    setActiveOrder(updated.data);
  };

  const sendKitchen = async () => {
    if (!activeOrder) return;
    const r = await api.post(`/orders/${activeOrder.id}/send-to-kitchen`);
    setActiveOrder(r.data);
    toast.success(lang === 'es' ? 'Enviado a cocina' : 'Enviado à cozinha');
    // Auto-print kitchen ticket if enabled in settings
    if (autoPrint) {
      const url = apiPdfUrl(`/orders/${activeOrder.id}/kitchen-pdf?size=${printerSize}`);
      printPdfInIframe(url);
    }
  };

  const printKitchen80 = () => {
    if (!activeOrder) return;
    printPdfInIframe(apiPdfUrl(`/orders/${activeOrder.id}/kitchen-pdf?size=80mm`));
  };

  const printKitchenA4 = () => {
    if (!activeOrder) return;
    window.open(apiPdfUrl(`/orders/${activeOrder.id}/kitchen-pdf?size=a4`), '_blank');
  };

  const closeOrder = async () => {
    if (!activeOrder) return;
    try {
      const r = await api.post(`/orders/${activeOrder.id}/close`, closePayload);
      toast.success(lang === 'es' ? `Ticket ${r.data.numero} emitido` : `Ticket ${r.data.numero} emitido`);
      window.open(apiPdfUrl(`/invoices/${r.data.id}/pdf`), '_blank');
      setActiveOrder(null);
      setCloseOpen(false);
      loadTables();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const createTable = async (form) => {
    await api.post('/tables', form);
    setCreateTableOpen(false);
    loadTables();
  };

  const filteredItems = useMemo(
    () => (activeCategory ? menuItems.filter((m) => m.categoria_id === activeCategory) : menuItems),
    [menuItems, activeCategory]
  );

  // ---- Active Order View ----
  if (activeOrder) {
    return (
      <div className="flex flex-col h-[calc(100vh-3.5rem)]" data-testid="pos-order-view">
        <div className="flex items-center gap-3 px-4 py-2 border-b bg-[hsl(var(--card))]">
          <Button variant="ghost" size="sm" onClick={() => { setActiveOrder(null); loadTables(); }} data-testid="pos-back-button">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <div className="font-heading font-semibold">
              {activeOrder.tipo === 'mesa'
                ? `${t('tables')} ${activeOrder.table_numero}`
                : activeOrder.tipo}
              {' • '}#{activeOrder.numero}
            </div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">{activeOrder.estado}</div>
          </div>
          <Badge variant="outline" className="font-mono">
            {formatCurrency(activeOrder.total, lang)}
          </Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr_360px] flex-1 overflow-hidden">
          {/* Categories */}
          <div className="border-r overflow-y-auto bg-[hsl(var(--card))] p-2">
            <div className="text-xs uppercase font-semibold text-[hsl(var(--muted-foreground))] px-2 py-1">{t('category')}</div>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveCategory(c.id)}
                className={cn(
                  'w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium mb-1',
                  activeCategory === c.id ? 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]' : 'hover:bg-[hsl(var(--muted))]'
                )}
                data-testid={`pos-category-${c.nombre.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {c.nombre}
              </button>
            ))}
            {categories.length === 0 && (
              <p className="text-xs text-[hsl(var(--muted-foreground))] px-2 py-4">
                {lang === 'es' ? 'Crea categorías en Menú.' : 'Crie categorias em Cardápio.'}
              </p>
            )}
          </div>

          {/* Items */}
          <div className="overflow-y-auto p-3 grid grid-cols-2 sm:grid-cols-3 gap-2 content-start">
            {filteredItems.map((mi) => (
              <button
                key={mi.id}
                onClick={() => addItem(mi)}
                className="rounded-xl border p-3 text-left bg-[hsl(var(--card))] hover:shadow-md hover:-translate-y-0.5 transition-transform active:scale-[0.98]"
                data-testid={`pos-menu-item-${mi.id}`}
              >
                <div className="font-medium text-sm leading-tight mb-1 line-clamp-2">{mi.nombre}</div>
                <div className="flex items-center justify-between mt-2">
                  <Badge variant="outline" className="text-[10px]">{mi.iva_pct}% IVA</Badge>
                  <div className="font-mono font-semibold text-sm">{formatCurrency(mi.pvp, lang)}</div>
                </div>
              </button>
            ))}
            {filteredItems.length === 0 && (
              <p className="col-span-full text-center text-sm text-[hsl(var(--muted-foreground))] py-12">
                {lang === 'es' ? 'Sin platos en esta categoría' : 'Sem pratos nesta categoria'}
              </p>
            )}
          </div>

          {/* Ticket */}
          <div className="border-l bg-[hsl(var(--card))] flex flex-col">
            <div className="p-3 border-b">
              <div className="font-heading font-semibold">{t('new_order')}</div>
            </div>
            <ScrollArea className="flex-1 p-2">
              {(activeOrder.items || []).length === 0 ? (
                <p className="text-center text-sm text-[hsl(var(--muted-foreground))] py-12">
                  {lang === 'es' ? 'Añade platos al pedido' : 'Adicione pratos ao pedido'}
                </p>
              ) : (
                activeOrder.items.map((it) => (
                  <div key={it.id} className="flex items-start gap-2 px-2 py-2 border-b last:border-0" data-testid={`pos-ticket-item-${it.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{it.nombre} <span className="text-xs text-[hsl(var(--muted-foreground))]">x{it.cantidad}</span></div>
                      {it.estado !== 'nuevo' && (
                        <div className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase">{t(`kds_status_${it.estado === 'preparando' ? 'preparing' : it.estado === 'listo' ? 'ready' : 'served'}`)}</div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm">{formatCurrency(it.pvp * it.cantidad, lang)}</div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeItem(it.id)} className="h-7 w-7" data-testid={`pos-ticket-remove-${it.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))
              )}
            </ScrollArea>
            <div className="p-3 border-t space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-[hsl(var(--muted-foreground))]">{t('base')}</span>
                <span className="font-mono">{formatCurrency(activeOrder.subtotal, lang)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-[hsl(var(--muted-foreground))]">{t('iva')}</span>
                <span className="font-mono">{formatCurrency(activeOrder.iva_total, lang)}</span>
              </div>
              <div className="flex justify-between font-heading font-bold text-lg">
                <span>{t('total')}</span>
                <span className="font-mono">{formatCurrency(activeOrder.total, lang)}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-2">
                <Button onClick={sendKitchen} size="lg" className="col-span-2" data-testid="pos-send-to-kitchen-button" disabled={!(activeOrder.items || []).length}>
                  <Send className="h-4 w-4 mr-2" />
                  {t('send_to_kitchen')} {autoPrint && <span className="text-[10px] opacity-70 ml-1">({lang === 'es' ? 'auto-impresión' : 'auto-impressão'})</span>}
                </Button>
                <Button onClick={printKitchen80} variant="outline" data-testid="pos-print-kitchen-80mm-button" disabled={!(activeOrder.items || []).length} title="Imprimir 80mm térmica">
                  <Printer className="h-4 w-4 mr-2" />
                  80mm
                </Button>
                <Button onClick={printKitchenA4} variant="outline" data-testid="pos-print-kitchen-pdf-button" disabled={!(activeOrder.items || []).length} title="PDF A4">
                  <Printer className="h-4 w-4 mr-2" />
                  A4
                </Button>
                <Button onClick={() => setCloseOpen(true)} variant="secondary" className="col-span-2" data-testid="pos-close-order-button" disabled={!(activeOrder.items || []).length}>
                  <CreditCard className="h-4 w-4 mr-2" />
                  {t('close_order')}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Close dialog */}
        <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
          <DialogContent data-testid="pos-close-dialog">
            <DialogHeader>
              <DialogTitle>{t('close_order')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('invoice_type')}</Label>
                  <Select value={closePayload.tipo} onValueChange={(v) => setClosePayload({ ...closePayload, tipo: v })}>
                    <SelectTrigger data-testid="pos-close-invoice-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ticket">{t('invoice_type_ticket')}</SelectItem>
                      <SelectItem value="factura_simplificada">{t('invoice_type_simplificada')}</SelectItem>
                      <SelectItem value="factura_completa">{t('invoice_type_completa')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('payment_method')}</Label>
                  <Select value={closePayload.pago_metodo} onValueChange={(v) => setClosePayload({ ...closePayload, pago_metodo: v })}>
                    <SelectTrigger data-testid="pos-close-payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="efectivo">{t('cash')}</SelectItem>
                      <SelectItem value="tarjeta">{t('card')}</SelectItem>
                      <SelectItem value="bizum">{t('bizum')}</SelectItem>
                      <SelectItem value="transferencia">{t('transfer')}</SelectItem>
                      <SelectItem value="otro">{t('other')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {closePayload.tipo === 'factura_completa' && (
                <>
                  <div>
                    <Label>{t('customer_name')}</Label>
                    <Input value={closePayload.cliente_nombre} onChange={(e) => setClosePayload({ ...closePayload, cliente_nombre: e.target.value })} data-testid="pos-close-client-name" />
                  </div>
                  <div>
                    <Label>{t('customer_nif')}</Label>
                    <Input value={closePayload.cliente_nif} onChange={(e) => setClosePayload({ ...closePayload, cliente_nif: e.target.value })} data-testid="pos-close-client-nif" />
                  </div>
                  <div>
                    <Label>{t('customer_address')}</Label>
                    <Input value={closePayload.cliente_direccion} onChange={(e) => setClosePayload({ ...closePayload, cliente_direccion: e.target.value })} data-testid="pos-close-client-address" />
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCloseOpen(false)}>{t('cancel')}</Button>
              <Button onClick={closeOrder} data-testid="pos-close-confirm-button">
                <ReceiptIcon className="h-4 w-4 mr-2" />
                {t('confirm')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ---- Tables grid ----
  return (
    <div className="p-4 sm:p-6" data-testid="pos-tables-view">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">{t('tables')}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('nav_pos')}</p>
        </div>
        <Dialog open={createTableOpen} onOpenChange={setCreateTableOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="pos-create-table-button">
              <Plus className="h-4 w-4" />
              {t('create_table')}
            </Button>
          </DialogTrigger>
          <CreateTableDialog onCreate={createTable} t={t} />
        </Dialog>
      </div>

      {tables.length === 0 ? (
        <Card className="max-w-md mx-auto mt-12">
          <CardContent className="text-center p-8">
            <div className="h-14 w-14 rounded-full bg-[hsl(var(--accent))] grid place-items-center mx-auto mb-3">
              <ReceiptIcon className="h-7 w-7 text-[hsl(var(--primary))]" />
            </div>
            <h3 className="font-heading font-semibold mb-1">{t('no_tables')}</h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
              {lang === 'es' ? 'Empieza creando tu primera mesa.' : 'Comece criando sua primeira mesa.'}
            </p>
            <Button onClick={() => setCreateTableOpen(true)} data-testid="pos-empty-create-button">
              <Plus className="h-4 w-4 mr-2" />
              {t('create_table')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {tables.map((table) => (
            <TableTile key={table.id} table={table} onClick={() => openTable(table)} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}

function CreateTableDialog({ onCreate, t }) {
  const [form, setForm] = useState({ numero: '', nombre: '', capacidad: 4, zona: 'Salón' });
  return (
    <DialogContent data-testid="pos-create-table-dialog">
      <DialogHeader>
        <DialogTitle>{t('create_table')}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label>{t('table_number')}</Label>
          <Input value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} data-testid="create-table-number-input" />
        </div>
        <div>
          <Label>{t('name')}</Label>
          <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="create-table-name-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>{t('capacity')}</Label>
            <Input type="number" min={1} value={form.capacidad} onChange={(e) => setForm({ ...form, capacidad: Number(e.target.value) })} data-testid="create-table-capacity-input" />
          </div>
          <div>
            <Label>{t('zone')}</Label>
            <Input value={form.zona} onChange={(e) => setForm({ ...form, zona: e.target.value })} data-testid="create-table-zone-input" />
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={() => onCreate(form)} disabled={!form.numero} data-testid="create-table-confirm-button">{t('save')}</Button>
      </DialogFooter>
    </DialogContent>
  );
}
